/**
 * useSavedReports.js
 * Manages saved report list, draft/approval workflow, and bulk operations.
 * Migrated to React Query — server state via useQuery, mutations use qc.invalidateQueries.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { QK } from '../../lib/queryKeys';
import { fetchReportsSaved } from '../../lib/queryFunctions';
import { toast } from 'sonner';
import api from '../../services/api';

export function useSavedReports() {
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: savedReports = [], isLoading: loadingSaved, refetch: fetchSavedReports } = useQuery({
    queryKey: QK.savedReports(currentUser?.tenant_id),
    queryFn: fetchReportsSaved(),
    enabled: !!currentUser?.tenant_id,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: QK.savedReports(currentUser?.tenant_id) });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [savedSearch, setSavedSearch] = useState('');
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [draftSearch, setDraftSearch] = useState('');
  const [previewDraft, setPreviewDraft] = useState(null);

  const toggleSelectReport = (id) =>
    setSelectedReportIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const selectedDrafts = savedReports.filter(
    r => selectedReportIds.includes(r.id) && r.report_status === 'draft'
  );
  const selectedApproved = savedReports.filter(
    r => selectedReportIds.includes(r.id) && r.report_status !== 'draft' && !r.emailed && !r.published_to_portal
  );

  const handleBulkDelete = async () => {
    const draftIds = selectedReportIds.filter(id =>
      savedReports.find(r => r.id === id && r.report_status === 'draft')
    );
    if (!draftIds.length) return;
    try {
      await api.post('/reports/saved/bulk-delete', { report_ids: draftIds });
      setSelectedReportIds([]);
      invalidate();
    } catch { toast.error('Failed to delete reports'); }
  };

  const handleBulkApprove = async () => {
    if (!selectedDrafts.length) return;
    try {
      await api.post('/dsu-automation/bulk-approve', { report_ids: selectedDrafts.map(r => r.id) });
      toast.success(`${selectedDrafts.length} report(s) approved`);
      setSelectedReportIds([]);
      invalidate();
    } catch {
      toast.error('Failed to approve reports');
    }
  };

  const handleBulkSend = async () => {
    if (!selectedApproved.length) return;
    setBulkSending(true);
    try {
      const res = await api.post('/dsu-automation/bulk-send', {
        report_ids: selectedApproved.map(r => r.id),
        delivery: 'both',
      });
      const d = res.data;
      toast.success(`Sent ${d.sent} report(s)${d.errors?.length ? `, ${d.errors.length} error(s)` : ''}`);
      setSelectedReportIds([]);
      invalidate();
    } catch {
      toast.error('Failed to send reports');
    }
    setBulkSending(false);
  };

  return {
    savedReports,
    loadingSaved,
    savedSearch, setSavedSearch,
    fetchSavedReports,
    selectedReportIds,
    toggleSelectReport,
    setSelectedReportIds,
    selectedDrafts,
    selectedApproved,
    handleBulkApprove,
    handleBulkDelete,
    bulkSending,
    handleBulkSend,
    draftSearch, setDraftSearch,
    previewDraft, setPreviewDraft,
  };
}
